import { useState } from 'react';
import { useAuth } from '../../../hooks/auth';
import type { SnapshotHistoryEntry } from '../../../types';
import { downloadHistoricalMigration } from '../../../services/Migration/migrationHistoryService';
import { useToast } from '../../Toast/ToastProvider';
import { parseTimestampToken } from '../../../utils/timestamp';
import { formatUtcTimestampLocal } from '../../../utils/formatters';
import { ui } from '../../../config/uiSizes';
import { logger } from '../../../utils/logger';

interface MigrationHistoryTableProps {
    history: SnapshotHistoryEntry[];
    migrationId: string;
}

export function MigrationHistoryTable({ history, migrationId }: MigrationHistoryTableProps) {
    const { accessToken } = useAuth();
    const { showToast } = useToast();
    const [downloadingTimestamp, setDownloadingTimestamp] = useState<string | null>(null);

    const handleDownload = async (entry: SnapshotHistoryEntry) => {
        try {
            // The timestamp is always the last segment of the blobPath (source of truth)
            const timestamp = entry.blobPath.split('/').pop() ?? '';

            setDownloadingTimestamp(timestamp);

            if (!accessToken) {
                throw new Error('No access token available');
            }

            await downloadHistoricalMigration(accessToken, migrationId, timestamp);
            showToast('Migration run downloaded successfully', 'success');
        } catch (error) {
            logger.error('Error downloading migration run', error instanceof Error ? error : new Error(String(error)));
            showToast('Failed to download migration run', 'error');
        } finally {
            setDownloadingTimestamp(null);
        }
    };

    const formatDateTime = (dateOrString: string | Date) => {
        const date = typeof dateOrString === 'string' ? dateOrString : dateOrString.toISOString();
        return formatUtcTimestampLocal(date);
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return 'N/A';
        const mb = bytes / 1024 / 1024;
        return `${mb.toFixed(2)} MB`;
    };

    const getStatusBadgeClasses = (status: string) => {
        const baseClasses = `${ui.input.modal} rounded ${ui.text.label} font-medium`;
        switch (status.toLowerCase()) {
            case 'complete':
                return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
            case 'failed':
                return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
        }
    };

    if (history.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No historical migration runs available
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className={`${ui.widthAndHeight.scrollableHistory}`}>
                <table className={`w-full text-left ${ui.text.tableCell}`}>
                    <thead className="sticky top-0 z-10 bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th
                                className={`${ui.input.base} ${ui.text.label} font-semibold uppercase tracking-wider`}
                            >
                                Run Date/Time
                            </th>
                            <th
                                className={`${ui.input.base} ${ui.text.label} font-semibold uppercase tracking-wider`}
                            >
                                Status
                            </th>
                            <th
                                className={`${ui.input.base} ${ui.text.label} font-semibold uppercase tracking-wider`}
                            >
                                Files
                            </th>
                            <th
                                className={`${ui.input.base} ${ui.text.label} font-semibold uppercase tracking-wider`}
                            >
                                Size
                            </th>
                            <th
                                className={`${ui.input.base} ${ui.text.label} font-semibold uppercase tracking-wider`}
                            ></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                        {[...history]
                            .sort(
                                (a, b) => new Date(b.capturedAtUtc).getTime() - new Date(a.capturedAtUtc).getTime()
                            )
                            .map((entry, index) => {
                                // The timestamp is always the last segment of the blobPath (source of truth)
                                const timestamp = entry.blobPath.split('/').pop() ?? '';
                                const parsed = parseTimestampToken(timestamp);
                                const displayDate = parsed ?? new Date(entry.capturedAtUtc);
                                const isDownloading = downloadingTimestamp === timestamp;

                                return (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                    >
                                        <td className={`${ui.input.base} text-gray-900 dark:text-gray-100`}>
                                            {formatDateTime(displayDate)}
                                        </td>
                                        <td className={`${ui.input.base}`}>
                                            <span className={getStatusBadgeClasses(entry.status)}>{entry.status}</span>
                                        </td>
                                        <td className={`${ui.input.base} text-gray-700 dark:text-gray-300`}>
                                            {entry.fileCount ?? 'N/A'}
                                        </td>
                                        <td className={`${ui.input.base} text-gray-700 dark:text-gray-300`}>
                                            {formatFileSize(entry.totalBytes)}
                                        </td>
                                        <td className={`${ui.input.base} text-right`}>
                                            <button
                                                onClick={() => handleDownload(entry)}
                                                disabled={isDownloading || entry.status.toLowerCase() !== 'complete'}
                                                className={`${ui.input.custom} ${ui.text.label} font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-600 transition-colors`}
                                            >
                                                {isDownloading ? 'Downloading...' : 'Download'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
